export function normalizeHierarchyRoleCode(code) {
  return String(code || "")
    .trim()
    .toUpperCase();
}

export function isTeamMemberRole(roleCode) {
  const code = normalizeHierarchyRoleCode(roleCode);
  return code === "TEAM_MEMBER" || code === "TM";
}

export function isUnrestrictedHierarchyRole(roleCode, isAdmin = false) {
  if (isAdmin) {
    return true;
  }

  const code = normalizeHierarchyRoleCode(roleCode);
  return code === "DIRECTOR" || code === "ADMIN";
}

export function isMemberLikeRole(roleCode) {
  return !roleCode || isTeamMemberRole(roleCode);
}

export function canSeeInboxGroups(roleCode, isAdmin = false) {
  if (isAdmin) {
    return true;
  }

  const code = normalizeHierarchyRoleCode(roleCode);
  return code === "DIRECTOR" || code === "SENIOR_MANAGER" || code === "ADMIN";
}

export function canSeeInboxOverview(roleCode, isAdmin = false) {
  if (isAdmin) {
    return true;
  }

  const code = normalizeHierarchyRoleCode(roleCode);
  return (
    code === "DIRECTOR" ||
    code === "ADMIN" ||
    code === "SENIOR_MANAGER" ||
    code === "MANAGER"
  );
}

export function formatAssignedTo(value) {
  const raw = String(value || "").trim();
  if (!raw) {
    return "Unassigned";
  }

  const angled = raw.match(/^(.*?)\s*<([^>]+)>\s*$/);
  if (angled) {
    return angled[1].trim() || angled[2].trim();
  }

  return raw;
}

export function assignedInitials(value) {
  const name = formatAssignedTo(value);
  if (!name || name === "Unassigned") {
    return "";
  }

  const cleaned = name.replace(/^IDX\./i, "").trim();
  const source = cleaned.includes("@")
    ? cleaned.split("@")[0].replace(/[._-]+/g, " ")
    : cleaned;
  const parts = source.split(/\s+/).filter(Boolean);
  if (!parts.length) {
    return "";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export function parseAssignedToEmail(value) {
  const raw = String(value || "").trim();
  if (!raw) {
    return "";
  }

  const angled = raw.match(/<([^>]+)>/);
  if (angled) {
    return String(angled[1] || "")
      .toLowerCase()
      .trim();
  }

  return raw.includes("@") ? raw.toLowerCase().trim() : "";
}

export function showInboxAssignedColumn(layout) {
  return layout !== "member";
}

export function showInboxDepartmentColumn(layout) {
  return layout === "org" || layout === "senior";
}

export function pendingQueueTitle(
  layout,
  { verified = false, managerView = "team" } = {},
) {
  if (verified) {
    if (layout === "manager") {
      return managerView === "mine" ? "My Verified Records" : "Team Verified Records";
    }
    if (layout === "member") {
      return "My Verified Records";
    }
    return "Verified Metadata Queue";
  }

  if (layout === "manager") {
    return managerView === "mine" ? "My Items" : "My Team Items";
  }

  if (layout === "member") {
    return "My Pending Items";
  }

  return "Pending Metadata Queue";
}

export function pendingQuickFilters(layout) {
  if (layout === "member") {
    return [
      { id: "all", label: "All" },
      { id: "older", label: "Older than 24h", older_than_24h: true },
    ];
  }

  if (layout === "manager") {
    return [
      { id: "all", label: "All" },
      { id: "older", label: "Older than 24h", older_than_24h: true },
    ];
  }

  if (layout === "senior") {
    return [
      { id: "all", label: "All" },
      { id: "assigned", label: "Assigned", assigned_state: "assigned" },
      { id: "older", label: "Older than 24h", older_than_24h: true },
    ];
  }

  return [
    { id: "all", label: "All" },
    { id: "unassigned", label: "Unassigned", assigned_state: "unassigned" },
    { id: "assigned", label: "Assigned", assigned_state: "assigned" },
    { id: "older", label: "Older than 24h", older_than_24h: true },
  ];
}

export function isPendingQuickFilterActive(filter, { assignedState, olderThan24h, activeTab }) {
  if (filter.tab === "verified") {
    return activeTab === "verified";
  }

  if (filter.id === "all") {
    return assignedState === "all" && !olderThan24h;
  }

  if (filter.older_than_24h) {
    return Boolean(olderThan24h);
  }

  return assignedState === (filter.assigned_state || "all") && !olderThan24h;
}

export function verifiedQuickFilters() {
  return [];
}

export function inboxQueueEmptyCopy({
  roleCode,
  isAdmin = false,
  verified = false,
} = {}) {
  if (isUnrestrictedHierarchyRole(roleCode, isAdmin)) {
    return verified
      ? {
          title: "No Verified Metadata",
          sub: "No verified records found.",
        }
      : {
          title: "No Pending Metadata",
          sub: "All records are already reviewed and verified.",
        };
  }

  if (isMemberLikeRole(roleCode)) {
    return verified
      ? {
          title: "No verified items assigned to you",
          sub: "Verified documents assigned to you will appear here.",
        }
      : {
          title: "No items assigned to you",
          sub: "When a document is assigned to you, it will show up here.",
        };
  }

  return verified
    ? {
        title: "No verified items for your team",
        sub: "Verified documents assigned to you or your reporting team will appear here.",
      }
    : {
        title: "No items for your team",
        sub: "Nothing is currently assigned to you or your reporting team.",
      };
}
